import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import StickyQuoteButton from "@/components/feature/StickyQuoteButton";
import { useSEO } from "@/hooks/useSEO";

const products = [
  {
    id: "eda10a",
    brand: "Honeywell",
    name: "ScanPal EDA10A",
    badge: "Best for Warehousing",
    tagline: "10\" Android Enterprise Tablet — 5G, Wi-Fi 6, 8000mAh, IP65",
    image: "https://readdy.ai/api/search-image?query=Honeywell%20ScanPal%20EDA10A%20rugged%20Android%20enterprise%20tablet%2010%20inch%20barcode%20scanner%20warehouse%20logistics%20yellow%20black%20industrial%20device%20clean%20white%20studio%20background%20product%20photography%20high%20detail%20professional&width=500&height=500&seq=rt-eda10a&orientation=squarish",
    specs: [
      { label: "OS", value: "Android 12 → 14" },
      { label: "Display", value: "10.1\" FHD 550-nit Gorilla Glass 5" },
      { label: "Connectivity", value: "5G + Wi-Fi 6 + NFC" },
      { label: "Durability", value: "IP65 · MIL-STD-810H · 1.3m drop" },
      { label: "Battery", value: "8,000mAh hot-swap · 12+ hrs" },
      { label: "Scanner", value: "Honeywell S0703 1D/2D Imager" },
    ],
    highlights: ["674g lightweight design", "Glove, wet & oily hand touch", "16MP rear + 8MP front cameras", "Honeywell Mobility Edge platform"],
    useCase: "Warehouse picking, receiving, inventory management, and last-mile delivery.",
  },
  {
    id: "rt10-android",
    brand: "Honeywell",
    name: "RT10 Android Tablet",
    badge: "Best for Logistics",
    tagline: "10\" Android Rugged Tablet — FlexRange Scanner, Hot-Swap, Vehicle Dock",
    image: "https://readdy.ai/api/search-image?query=Honeywell%20RT10%20rugged%20Android%20tablet%2010%20inch%20enterprise%20warehouse%20vehicle%20mount%20dock%20IP65%20MIL-SPEC%20industrial%20device%20clean%20white%20studio%20background%20product%20photography%20high%20detail%20professional&width=500&height=500&seq=rt-rt10a&orientation=squarish",
    specs: [
      { label: "OS", value: "Android 9 → 13 (committed to 14)" },
      { label: "Display", value: "10.1\" 800-nit optically bonded" },
      { label: "Connectivity", value: "4G LTE CAT13 · Wi-Fi 5 · NFC" },
      { label: "Durability", value: "IP65 · MIL-STD-810G · 1.2m drop" },
      { label: "Battery", value: "5,900mAh (12hr) or 10,280mAh (18hr) hot-swap" },
      { label: "Scanner", value: "FlexRange 1D/2D · 0.1m to 10.7m" },
    ],
    highlights: ["FlexRange scans 5 inches to 35 feet", "Vehicle dock compatible (forklift)", "Smart Talk PTT built-in", "Zero-downtime hot-swap battery"],
    useCase: "Forklift operations, fleet management, cross-docking, and transport logistics.",
  },
  {
    id: "rt10-windows",
    brand: "Honeywell",
    name: "RT10 Windows Tablet",
    badge: "Best for Legacy Apps",
    tagline: "10\" Windows Rugged Tablet — Full Desktop Apps, FlexRange Scanner",
    image: "https://readdy.ai/api/search-image?query=Honeywell%20RT10%20rugged%20Windows%20tablet%2010%20inch%20enterprise%20field%20operations%20glove%20touch%20IP65%20MIL-SPEC%20industrial%20device%20clean%20white%20studio%20background%20product%20photography%20high%20detail%20professional&width=500&height=500&seq=rt-rt10w&orientation=squarish",
    specs: [
      { label: "OS", value: "Windows 10 Enterprise LTSC" },
      { label: "Display", value: "10.1\" 800-nit optically bonded" },
      { label: "Connectivity", value: "4G LTE CAT6 · Wi-Fi 5 · GPS" },
      { label: "Durability", value: "IP65 · MIL-STD-810G · 1.2m drop" },
      { label: "Battery", value: "5,900mAh (12hr) or 10,280mAh (18hr) hot-swap" },
      { label: "Scanner", value: "FlexRange 1D/2D · 0.1m to 10.7m" },
    ],
    highlights: ["Full Windows desktop + legacy apps", "TPM v2.0 enterprise security", "Kensington Lock slot", "USB 3.0 + HDMI + microSD"],
    useCase: "ERP, WMS, and legacy Windows applications in warehouse and field environments.",
  },
  {
    id: "dell-rugged-10",
    brand: "Dell Rugged",
    name: "Dell Pro Rugged 10",
    badge: "Best for Field Service",
    tagline: "World's Lightest 10\" Fully-Rugged Windows Tablet — 1.04kg, IP66, Wi-Fi 7",
    image: "https://readdy.ai/api/search-image?query=Dell%20rugged%20pro%2010%20inch%20tablet%20Windows%20enterprise%20field%20rugged%20reinforced%20casing%20MIL-SPEC%20outdoor%20worker%20glove%20touch%20clean%20white%20studio%20background%20product%20photography%20high%20detail%20durable%20tablet%20device%20professional&width=500&height=500&seq=rt-dell10&orientation=squarish",
    specs: [
      { label: "OS", value: "Windows 11 Pro (Copilot+ PC)" },
      { label: "Display", value: "10\" 1000-nit sunlight-readable" },
      { label: "Processor", value: "Intel Core Ultra 200V + 40+ TOPS NPU" },
      { label: "Durability", value: "MIL-STD-810H · IP66 · FirstNet Ready" },
      { label: "Weight", value: "1.04kg — world's lightest 10\" rugged" },
      { label: "Connectivity", value: "Wi-Fi 7 · optional 5G · BT" },
    ],
    highlights: ["World's lightest 10\" fully-rugged tablet", "Copilot+ AI PC capabilities", "Hot-swappable all-day battery", "Dell ProManage fleet management"],
    useCase: "Field service, defence, public safety, and first responder operations.",
  },
  {
    id: "dell-rugged-12",
    brand: "Dell Rugged",
    name: "Dell Pro Rugged 12",
    badge: "Best for Outdoor",
    tagline: "12\" Fully-Rugged Tablet — 1200-nit Display, Dual Hot-Swap Battery",
    image: "https://readdy.ai/api/search-image?query=Dell%20rugged%20pro%2012%20inch%20tablet%20outdoor%20military%20grade%20reinforced%20casing%20MIL-SPEC%20field%20engineer%20glove%20touch%20hot%20swap%20battery%20clean%20white%20background%20product%20photography%20durable%20reinforced%20tablet%20high%20detail%20professional&width=500&height=500&seq=rt-dell12&orientation=squarish",
    specs: [
      { label: "OS", value: "Windows 11 Pro (Copilot+ PC)" },
      { label: "Display", value: "12\" 1200-nit sunlight-readable" },
      { label: "Processor", value: "Intel Core Ultra 200V + 40+ TOPS NPU" },
      { label: "Durability", value: "MIL-STD-810H · IP66 · FirstNet Ready" },
      { label: "Battery", value: "Dual hot-swappable batteries" },
      { label: "Connectivity", value: "Wi-Fi 7 · optional 5G · BT" },
    ],
    highlights: ["1200-nit — readable in direct sunlight", "Dual hot-swap batteries", "Optional keyboard dock (laptop mode)", "FirstNet Ready Band 14"],
    useCase: "Outdoor field operations, construction, utilities, and defence.",
  },
];

const industries = [
  {
    icon: "ri-archive-line",
    title: "Warehousing & Logistics",
    desc: "Honeywell ScanPal EDA10A and RT10 tablets power warehouse picking, receiving, and inventory operations with FlexRange scanning up to 35ft and all-day hot-swap batteries for 24/7 operations.",
    devices: ["ScanPal EDA10A", "RT10 Android"],
    stat: "40% faster picking accuracy",
  },
  {
    icon: "ri-truck-line",
    title: "Transport & Fleet",
    desc: "Honeywell RT10 tablets mount to forklifts and fleet vehicles via vehicle docks, providing drivers with WMS, navigation, and PTT communication tools without leaving the cab.",
    devices: ["RT10 Android", "RT10 Windows"],
    stat: "Zero device downtime per shift",
  },
  {
    icon: "ri-building-line",
    title: "Construction & Field Services",
    desc: "Dell Pro Rugged 10 and 12 tablets give field engineers access to BIM, CAD, and project management tools on-site — surviving 6ft drops, dust, and rain that destroy consumer devices.",
    devices: ["Dell Pro Rugged 10", "Dell Pro Rugged 12"],
    stat: "3x longer device lifespan",
  },
  {
    icon: "ri-government-line",
    title: "Defence & Public Safety",
    desc: "Dell Pro Rugged tablets are deployed by defence and public safety agencies — FirstNet Ready with Band 14, IP66 rated, and operating from -20°F to 145°F in extreme environments.",
    devices: ["Dell Pro Rugged 10", "Dell Pro Rugged 12"],
    stat: "MIL-STD-810H certified",
  },
  {
    icon: "ri-hospital-line",
    title: "Healthcare & Clinical",
    desc: "Rugged tablets in clinical environments need to survive disinfectant wipes, drops, and 24/7 shift use. Honeywell and Dell Rugged devices meet healthcare-grade durability requirements.",
    devices: ["ScanPal EDA10A", "Dell Pro Rugged 10"],
    stat: "IP65/IP66 rated",
  },
  {
    icon: "ri-tools-line",
    title: "Manufacturing & Maintenance",
    desc: "Factory floor and maintenance teams use rugged tablets for work orders, asset tracking, and quality inspection — with glove-touch displays and hot-swap batteries for continuous operation.",
    devices: ["RT10 Android", "RT10 Windows"],
    stat: "Hot-swap · zero downtime",
  },
];

const comparison = [
  { feature: "Best For", honeywell: "Warehousing, logistics, scanning", dell: "Field service, defence, outdoor" },
  { feature: "OS", honeywell: "Android 12–14 / Windows 10 LTSC", dell: "Windows 11 Pro (Copilot+ PC)" },
  { feature: "Barcode Scanner", honeywell: "Built-in FlexRange 1D/2D (0.1–10.7m)", dell: "Optional external scanner" },
  { feature: "Vehicle Dock", honeywell: "Yes — forklift & fleet dock", dell: "Yes — Havis, PMT, Gamber Johnson" },
  { feature: "Battery", honeywell: "Hot-swap · 12–18hr", dell: "Hot-swap · all-day" },
  { feature: "Durability", honeywell: "IP65 · MIL-STD-810G/H · 1.2–1.3m drop", dell: "IP66 · MIL-STD-810H · 1.8m drop" },
  { feature: "Display Brightness", honeywell: "550–800 nit", dell: "1000–1200 nit (sunlight-readable)" },
  { feature: "AI / NPU", honeywell: "Standard Qualcomm / Intel", dell: "Intel Core Ultra 200V · 40+ TOPS NPU" },
  { feature: "Connectivity", honeywell: "4G LTE / 5G · Wi-Fi 5/6 · NFC", dell: "Wi-Fi 7 · optional 5G · FirstNet" },
  { feature: "Fleet Management", honeywell: "Honeywell Mobility Edge", dell: "Dell ProManage" },
];

const faqs = [
  {
    q: "What is the best rugged tablet for warehouse operations in Malaysia?",
    a: "For warehouse and logistics operations, we recommend the Honeywell ScanPal EDA10A or RT10 Android tablet. Both feature built-in FlexRange barcode scanners (scanning from 5 inches to 35 feet), hot-swappable batteries for 24/7 shift operations, and vehicle dock compatibility for forklift mounting. The EDA10A is lighter (674g) and supports 5G, while the RT10 offers longer battery life (up to 18 hours) and a wider range of accessories.",
  },
  {
    q: "Which rugged tablet is best for outdoor field service in Malaysia?",
    a: "For outdoor field service, construction, and defence applications, the Dell Pro Rugged 10 or 12 tablet is the better choice. The Dell Pro Rugged 10 is the world's lightest 10\" fully-rugged Windows tablet at 1.04kg, with a 1000-nit sunlight-readable display, IP66 rating, and MIL-STD-810H certification. The Dell Pro Rugged 12 adds a 1200-nit display and dual hot-swap batteries for extended outdoor operations.",
  },
  {
    q: "Do Honeywell rugged tablets support vehicle mounting in Malaysia?",
    a: "Yes. Honeywell RT10 tablets are compatible with vehicle docks for forklift and fleet vehicle mounting. The docks keep the tablet powered, connected, and secure while driving, with hot-swap charging support. SE Distribution supplies both the tablets and compatible vehicle docking solutions for Malaysian logistics and transport operations.",
  },
  {
    q: "What is the difference between Honeywell and Dell rugged tablets?",
    a: "Honeywell rugged tablets (ScanPal EDA10A, RT10) are purpose-built for warehousing and logistics — they feature built-in barcode scanners, vehicle docks, and Android/Windows OS for WMS integration. Dell Pro Rugged tablets are designed for field service, defence, and outdoor operations — they run Windows 11 Pro with Intel Core Ultra processors, offer higher display brightness (1000–1200 nit), and carry IP66 ratings with FirstNet connectivity.",
  },
  {
    q: "Does SE Distribution provide rugged tablet deployment and support in Malaysia?",
    a: "Yes. SE Distribution is a technology partner for both Honeywell and Dell Rugged Pro in Malaysia. We provide pre-sales consultation, device configuration, MDM setup (Honeywell Mobility Edge / Dell ProManage), deployment, and ongoing after-sales support from our Kuala Lumpur office. Contact us for a free needs assessment and device recommendation.",
  },
  {
    q: "Can I get a demo of rugged tablets before purchasing?",
    a: "Yes. SE Distribution offers hands-on demos of Honeywell and Dell Rugged tablets at our Kuala Lumpur office, or we can arrange an on-site demo at your warehouse or facility. Contact our sales team at +603 6412 7917 or sales@sedistribution.com.my to schedule a demo.",
  },
];

export default function RuggedTabletPage() {
  useSEO({
    title: "Rugged Tablet Malaysia | Honeywell & Dell Rugged for Warehouse & Logistics | SE Distribution",
    description: "Malaysia's rugged tablet specialist — Honeywell ScanPal EDA10A, RT10 & Dell Pro Rugged 10/12. MIL-STD-810H, IP65/66, hot-swap batteries. Perfect for warehouse, logistics & field service. Free demo from SE Distribution KL.",
    canonical: "https://www.sedistribution.com.my/rugged-tablet-malaysia",
    keywords: "rugged tablet Malaysia, Honeywell rugged tablet Malaysia, Dell rugged tablet Malaysia, warehouse tablet Malaysia, logistics tablet Malaysia, ScanPal EDA10A Malaysia, RT10 tablet Malaysia, MIL-SPEC tablet Malaysia",
  });

  const [activeProduct, setActiveProduct] = useState(0);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formData, setFormData] = useState({ name: "", company: "", email: "", phone: "", device: "", message: "" });
  const [formStatus, setFormStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone) return;
    setFormStatus("sending");
    try {
      const body = new URLSearchParams({
        name: formData.name,
        company: formData.company,
        email: formData.email,
        phone: formData.phone,
        device: formData.device,
        message: formData.message,
      });
      const res = await fetch("https://readdy.ai/api/form/d7iucj11u44cjefeoro0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setFormStatus("sent");
      } else {
        setFormStatus("error");
      }
    } catch {
      setFormStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20">

        {/* Hero */}
        <section className="relative bg-[#1a1a1a] min-h-[620px] flex items-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=warehouse%20worker%20using%20rugged%20tablet%20scanning%20barcode%20inventory%20logistics%20operations%20industrial%20environment%20forklift%20shelves%20dramatic%20cinematic%20lighting%20professional%20photography%20wide%20angle%20high%20detail&width=1440&height=700&seq=rt-hero-bg&orientation=landscape"
            alt="Rugged tablet Malaysia warehouse logistics"
            className="absolute inset-0 w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/30"></div>
          <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-10 py-24">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-6">
                <span className="text-[10px] font-bold uppercase tracking-widest text-white/50 border border-white/20 px-3 py-1 rounded-full">Honeywell + Dell Rugged · Malaysia</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                Rugged Tablet<br />
                <span className="text-[#c8a96e]">Malaysia</span>
              </h1>
              <p className="text-lg md:text-xl text-white/70 leading-relaxed mb-8 max-w-2xl">
                Purpose-built rugged tablets for warehousing, logistics, and field service across Malaysia. Honeywell ScanPal and RT10 for scanning-intensive operations. Dell Pro Rugged for outdoor and defence deployments.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="#quote-form"
                  className="inline-flex items-center justify-center gap-2 bg-white text-[#1a1a1a] font-semibold px-8 py-4 rounded-lg hover:bg-[#f5f3ee] transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-file-list-3-line"></i>
                  Request a Quote
                </a>
                <Link
                  to="/rugged-spotlight"
                  className="inline-flex items-center justify-center gap-2 border border-white/30 text-white font-medium px-8 py-4 rounded-lg hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer"
                >
                  View Full Lineup
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
          {/* Stats bar */}
          <div className="absolute bottom-0 left-0 right-0 bg-white/5 backdrop-blur-sm border-t border-white/10">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { val: "5", label: "Rugged Models" },
                { val: "IP66", label: "Max Water Rating" },
                { val: "18hr", label: "Max Battery Life" },
                { val: "MIL-810H", label: "Ruggedness Standard" },
              ].map((s) => (
                <div key={s.label} className="text-center">
                  <p className="text-xl font-bold text-white">{s.val}</p>
                  <p className="text-xs text-white/50">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust bar */}
        <section className="bg-[#f8f7f4] border-b border-gray-100 py-4">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-wrap items-center justify-center gap-6 md:gap-10">
            {[
              { icon: "ri-verified-badge-line", text: "Honeywell Technology Partner" },
              { icon: "ri-computer-line", text: "Dell Rugged Pro Partner" },
              { icon: "ri-government-line", text: "Government Procurement Ready" },
              { icon: "ri-customer-service-2-line", text: "Local KL Support Team" },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${item.icon} text-[#1a1a1a] text-base`}></i>
                </div>
                <span className="text-xs font-medium text-[#1a1a1a] whitespace-nowrap">{item.text}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Product Lineup */}
        <section className="py-20 md:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-14">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Complete Lineup</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">Rugged Tablets Available in Malaysia</h2>
              <p className="text-gray-500 max-w-2xl mx-auto text-sm leading-relaxed">
                Five purpose-built rugged tablet models from Honeywell and Dell — from warehouse scanning to outdoor field service. Every device is MIL-SPEC certified with hot-swap batteries and local support.
              </p>
            </div>

            {/* Product tabs */}
            <div className="flex flex-wrap gap-2 justify-center mb-10">
              {products.map((p, i) => (
                <button
                  key={p.id}
                  onClick={() => setActiveProduct(i)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    activeProduct === i
                      ? "bg-[#1a1a1a] text-white"
                      : "bg-[#f5f3ee] text-[#1a1a1a] hover:bg-[#ede9e3]"
                  }`}
                >
                  {p.name}
                </button>
              ))}
            </div>

            {/* Active product detail */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
              <div className="relative">
                <div className="aspect-square max-w-md mx-auto bg-[#f8f7f4] rounded-2xl overflow-hidden flex items-center justify-center p-8">
                  <img
                    src={products[activeProduct].image}
                    alt={`${products[activeProduct].name} rugged tablet Malaysia`}
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="absolute top-4 left-4 bg-[#1a1a1a] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
                  {products[activeProduct].badge}
                </div>
                <div className="absolute top-4 right-4 bg-white text-[#1a1a1a] text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border border-gray-200">
                  {products[activeProduct].brand}
                </div>
              </div>
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-2">{products[activeProduct].name}</h3>
                <p className="text-gray-400 text-sm mb-1">{products[activeProduct].tagline}</p>
                <p className="text-gray-500 text-sm leading-relaxed mb-6 italic">{products[activeProduct].useCase}</p>

                <div className="grid grid-cols-2 gap-3 mb-6">
                  {products[activeProduct].specs.map((spec) => (
                    <div key={spec.label} className="bg-[#f8f7f4] rounded-lg p-3">
                      <p className="text-[10px] text-gray-400 mb-0.5 uppercase tracking-wider">{spec.label}</p>
                      <p className="text-xs font-semibold text-[#1a1a1a]">{spec.value}</p>
                    </div>
                  ))}
                </div>

                <div className="mb-8">
                  <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Key Highlights</p>
                  <ul className="space-y-2">
                    {products[activeProduct].highlights.map((h) => (
                      <li key={h} className="flex items-center gap-2 text-sm text-[#1a1a1a]">
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                          <i className="ri-check-line text-[#1a1a1a] text-sm font-bold"></i>
                        </div>
                        {h}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="#quote-form"
                    className="inline-flex items-center justify-center gap-2 bg-[#1a1a1a] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer text-sm"
                  >
                    Request a Quote
                  </a>
                  <Link
                    to="/rugged-spotlight"
                    className="inline-flex items-center justify-center gap-2 border border-gray-200 text-[#1a1a1a] font-medium px-6 py-3 rounded-lg hover:bg-[#f5f3ee] transition-colors whitespace-nowrap cursor-pointer text-sm"
                  >
                    Full Specs
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </div>

            {/* Product grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {products.map((p, i) => (
                <button
                  key={p.id}
                  onClick={() => setActiveProduct(i)}
                  className={`p-4 rounded-xl border text-left transition-all cursor-pointer ${
                    activeProduct === i
                      ? "border-[#1a1a1a] bg-[#f8f7f4]"
                      : "border-gray-100 hover:border-gray-200 bg-white"
                  }`}
                >
                  <div className="aspect-square bg-[#f5f3ee] rounded-lg mb-3 overflow-hidden flex items-center justify-center p-2">
                    <img src={p.image} alt={p.name} className="w-full h-full object-contain" />
                  </div>
                  <p className="text-xs font-bold text-[#1a1a1a]">{p.name}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">{p.brand}</p>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Honeywell vs Dell Comparison */}
        <section className="py-20 bg-[#f8f7f4]">
          <div className="max-w-5xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-14">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Which Brand is Right for You?</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">Honeywell vs Dell Rugged — Head to Head</h2>
              <p className="text-gray-500 text-sm max-w-xl mx-auto">Both are excellent rugged tablets — the right choice depends on your workflow. Here's how they compare.</p>
            </div>
            <div className="rounded-xl overflow-hidden border border-gray-200">
              <div className="grid grid-cols-3 bg-[#1a1a1a] px-6 py-4">
                <p className="text-xs font-bold text-white/60 uppercase tracking-widest">Feature</p>
                <p className="text-xs font-bold text-white/60 uppercase tracking-widest text-center">Honeywell</p>
                <p className="text-xs font-bold text-white uppercase tracking-widest text-center">Dell Rugged</p>
              </div>
              {comparison.map((row, i) => (
                <div
                  key={row.feature}
                  className={`grid grid-cols-3 px-6 py-4 border-b border-gray-100 ${i % 2 === 0 ? "bg-white" : "bg-[#fafaf9]"}`}
                >
                  <p className="text-sm font-semibold text-[#1a1a1a]">{row.feature}</p>
                  <p className="text-xs text-gray-500 text-center">{row.honeywell}</p>
                  <p className="text-xs font-medium text-[#1a1a1a] text-center">{row.dell}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl p-5 border border-gray-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#dc2626]/10 rounded-lg">
                    <i className="ri-archive-line text-[#dc2626] text-base"></i>
                  </div>
                  <p className="text-sm font-bold text-[#1a1a1a]">Choose Honeywell if...</p>
                </div>
                <ul className="space-y-1.5">
                  {["You need built-in barcode scanning", "Your team uses forklifts or fleet vehicles", "You run WMS or ERP on Android/Windows", "24/7 shift operations with hot-swap batteries"].map((t) => (
                    <li key={t} className="flex items-center gap-2 text-xs text-gray-600">
                      <i className="ri-check-line text-[#dc2626] flex-shrink-0"></i>
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-xl p-5 border border-gray-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a]/10 rounded-lg">
                    <i className="ri-sun-line text-[#1a1a1a] text-base"></i>
                  </div>
                  <p className="text-sm font-bold text-[#1a1a1a]">Choose Dell Rugged if...</p>
                </div>
                <ul className="space-y-1.5">
                  {["You work outdoors in direct sunlight", "You need Windows 11 Pro + AI capabilities", "Your environment requires IP66 + 1.8m drop", "Defence, public safety, or FirstNet required"].map((t) => (
                    <li key={t} className="flex items-center gap-2 text-xs text-gray-600">
                      <i className="ri-check-line text-[#1a1a1a] flex-shrink-0"></i>
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Industries */}
        <section className="py-20 md:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-14">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Industries We Serve</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">Rugged Tablet Use Cases in Malaysia</h2>
              <p className="text-gray-500 max-w-2xl mx-auto text-sm leading-relaxed">
                From warehouse floors to construction sites — rugged tablets are deployed across Malaysia's most demanding environments.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {industries.map((ind) => (
                <div key={ind.title} className="bg-[#f8f7f4] rounded-xl p-6 border border-gray-100">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#1a1a1a] rounded-xl">
                      <i className={`${ind.icon} text-white text-xl`}></i>
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 border border-gray-200 px-2 py-1 rounded-full bg-white">{ind.stat}</span>
                  </div>
                  <h3 className="text-sm font-bold text-[#1a1a1a] mb-2">{ind.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed mb-4">{ind.desc}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {ind.devices.map((d) => (
                      <span key={d} className="text-[10px] font-semibold bg-white border border-gray-200 text-[#1a1a1a] px-2 py-1 rounded-full">{d}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Quote Form */}
        <section id="quote-form" className="py-20 bg-[#1a1a1a]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              {/* Left: Info */}
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-3">Get a Quote</p>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-5 leading-tight">
                  Request a Rugged Tablet Quote
                </h2>
                <p className="text-white/60 text-sm leading-relaxed mb-8">
                  Tell us about your deployment — number of devices, use case, and environment. Our solutions team will recommend the right rugged tablet and provide a competitive quote within 1 business day.
                </p>
                <div className="space-y-5">
                  {[
                    { icon: "ri-time-line", title: "Response within 1 business day", desc: "Our sales team responds to all enquiries within 1 business day." },
                    { icon: "ri-map-pin-line", title: "On-site demo available", desc: "We can arrange a hands-on demo at your warehouse or facility in Malaysia." },
                    { icon: "ri-shield-check-line", title: "Genuine products · Full warranty", desc: "All devices are genuine with full manufacturer warranty and local support." },
                    { icon: "ri-customer-service-2-line", title: "Local KL support team", desc: "Dedicated technical team in Kuala Lumpur for deployment and after-sales support." },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start gap-4">
                      <div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-lg flex-shrink-0">
                        <i className={`${item.icon} text-white text-base`}></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-white mb-0.5">{item.title}</p>
                        <p className="text-xs text-white/50 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-8 pt-8 border-t border-white/10">
                  <p className="text-xs text-white/40 mb-3">Or contact us directly:</p>
                  <div className="flex flex-col gap-2">
                    <a href="tel:+60364127917" className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors">
                      <i className="ri-phone-line text-white/40"></i>
                      +603 6412 7917
                    </a>
                    <a href="mailto:sales@sedistribution.com.my" className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors">
                      <i className="ri-mail-line text-white/40"></i>
                      sales@sedistribution.com.my
                    </a>
                  </div>
                </div>
              </div>

              {/* Right: Form */}
              <div className="bg-white rounded-2xl p-8">
                {formStatus === "sent" ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 flex items-center justify-center bg-[#f8f7f4] rounded-full mx-auto mb-4">
                      <i className="ri-check-line text-[#1a1a1a] text-2xl"></i>
                    </div>
                    <h3 className="text-xl font-bold text-[#1a1a1a] mb-2">Quote Request Received!</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">
                      Thank you! Our sales team will review your requirements and get back to you within 1 business day with a competitive quote.
                    </p>
                  </div>
                ) : (
                  <form
                    data-readdy-form
                    onSubmit={handleFormSubmit}
                    className="space-y-4"
                  >
                    <div>
                      <h3 className="text-lg font-bold text-[#1a1a1a] mb-1">Request a Quote</h3>
                      <p className="text-xs text-gray-400">Fill in your details and we'll get back to you within 1 business day.</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Full Name *</label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Your name"
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-[#1a1a1a] placeholder-gray-400 outline-none focus:border-[#1a1a1a] transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Company</label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                          placeholder="Company name"
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-[#1a1a1a] placeholder-gray-400 outline-none focus:border-[#1a1a1a] transition-colors"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Email *</label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="your@email.com"
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-[#1a1a1a] placeholder-gray-400 outline-none focus:border-[#1a1a1a] transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Phone *</label>
                        <input
                          type="tel"
                          name="phone"
                          required
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="+60 12 345 6789"
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-[#1a1a1a] placeholder-gray-400 outline-none focus:border-[#1a1a1a] transition-colors"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Device of Interest</label>
                      <select
                        name="device"
                        value={formData.device}
                        onChange={(e) => setFormData({ ...formData, device: e.target.value })}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-[#1a1a1a] outline-none focus:border-[#1a1a1a] transition-colors cursor-pointer bg-white"
                      >
                        <option value="">Select a device (optional)</option>
                        <option value="Honeywell ScanPal EDA10A">Honeywell ScanPal EDA10A</option>
                        <option value="Honeywell RT10 Android">Honeywell RT10 Android</option>
                        <option value="Honeywell RT10 Windows">Honeywell RT10 Windows</option>
                        <option value="Dell Pro Rugged 10">Dell Pro Rugged 10 Tablet</option>
                        <option value="Dell Pro Rugged 12">Dell Pro Rugged 12 Tablet</option>
                        <option value="Not sure - need recommendation">Not sure — need recommendation</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#1a1a1a] mb-1.5">Tell us about your deployment</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={(e) => {
                          if (e.target.value.length <= 500) setFormData({ ...formData, message: e.target.value });
                        }}
                        placeholder="Number of devices, use case (warehouse, field service, etc.), environment, timeline..."
                        rows={4}
                        maxLength={500}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-[#1a1a1a] placeholder-gray-400 outline-none focus:border-[#1a1a1a] transition-colors resize-none"
                      />
                      <p className="text-[10px] text-gray-400 mt-1 text-right">{formData.message.length}/500</p>
                    </div>
                    <button
                      type="submit"
                      disabled={formStatus === "sending"}
                      className="w-full bg-[#1a1a1a] text-white font-semibold py-3.5 rounded-lg hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer text-sm disabled:opacity-60"
                    >
                      {formStatus === "sending" ? "Sending..." : "Submit Quote Request"}
                    </button>
                    {formStatus === "error" && (
                      <p className="text-xs text-red-500 text-center">Something went wrong. Please try again or call us directly.</p>
                    )}
                    <p className="text-[10px] text-gray-400 text-center">Mon–Fri, 9:30 AM – 6:30 PM (GMT+8) · No spam, ever.</p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 md:py-28 bg-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-14">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">FAQ</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">Rugged Tablet Questions</h2>
              <p className="text-gray-500 text-sm">Common questions from warehouse and logistics teams in Malaysia.</p>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div key={i} className="border border-gray-100 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-[#fafaf9] transition-colors"
                  >
                    <span className="text-sm font-semibold text-[#1a1a1a] pr-4">{faq.q}</span>
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i className={`${openFaq === i ? "ri-subtract-line" : "ri-add-line"} text-gray-400 text-base`}></i>
                    </div>
                  </button>
                  {openFaq === i && (
                    <div className="px-6 pb-5 border-t border-gray-50">
                      <p className="text-sm text-gray-500 leading-relaxed pt-4">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-[#f8f7f4] border-t border-gray-100">
          <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-[#1a1a1a] rounded-2xl mx-auto mb-6">
              <i className="ri-smartphone-line text-white text-2xl"></i>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">Ready to Deploy Rugged Tablets?</h2>
            <p className="text-gray-500 text-sm leading-relaxed mb-8 max-w-xl mx-auto">
              Talk to our team about the right rugged tablet for your warehouse or field operations — we handle everything from device selection to MDM setup and deployment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#quote-form"
                className="inline-flex items-center justify-center gap-2 bg-[#1a1a1a] text-white font-semibold px-8 py-4 rounded-lg hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-file-list-3-line"></i>
                Request a Quote
              </a>
              <a
                href="tel:+60364127917"
                className="inline-flex items-center justify-center gap-2 border border-gray-200 text-[#1a1a1a] font-medium px-8 py-4 rounded-lg hover:bg-white transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line"></i>
                +603 6412 7917
              </a>
            </div>
            <p className="text-xs text-gray-400 mt-6">Mon–Fri, 9:30 AM – 6:30 PM (GMT+8) · sales@sedistribution.com.my</p>
          </div>
        </section>

      </main>
      <Footer />
      <StickyQuoteButton formId="quote-form" label="Request a Quote" />
    </div>
  );
}
